import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

interface Upgrade {
  id: string;
  cost: number;
  perClick: number;
  perSecond: number;
  icon: string;
  owned: number;
}

export default function MoneyButtonSection() {
  const [balance, setBalance] = useState(0);
  const [perClick, setPerClick] = useState(1);
  const [perSecond, setPerSecond] = useState(0);
  const [clicks, setClicks] = useState(0);
  const [particles, setParticles] = useState<Array<{id: number; x: number; y: number}>>([]);
  const { t } = useTranslation();

  const [upgrades, setUpgrades] = useState<Upgrade[]>([
    { id: 'cursor', cost: 15, perClick: 1, perSecond: 0, icon: 'ri-cursor-fill', owned: 0 },
    { id: 'bot', cost: 100, perClick: 0, perSecond: 1, icon: 'ri-robot-2-line', owned: 0 },
    { id: 'server', cost: 500, perClick: 5, perSecond: 5, icon: 'ri-server-line', owned: 0 },
    { id: 'ai', cost: 2500, perClick: 10, perSecond: 25, icon: 'ri-brain-line', owned: 0 },
  ]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (perSecond > 0) {
        setBalance((prev) => prev + perSecond);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [perSecond]);

  const handleClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const btn = e.currentTarget;
    const rect = btn.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const id = Date.now();
    setParticles((prev) => [...prev.slice(-20), { id, x, y }]);
    setTimeout(() => {
      setParticles((prev) => prev.filter((p) => p.id !== id));
    }, 600);
    setBalance((prev) => prev + perClick);
    setClicks((prev) => prev + 1);
  }, [perClick]);

  const buyUpgrade = useCallback((upgradeId: string) => {
    const upgrade = upgrades.find((u) => u.id === upgradeId);
    if (!upgrade || balance < upgrade.cost) return;
    setBalance((prev) => prev - upgrade.cost);
    setPerClick((prev) => prev + upgrade.perClick);
    setPerSecond((prev) => prev + upgrade.perSecond);
    setUpgrades((prev) =>
      prev.map((u) =>
        u.id === upgradeId
          ? { ...u, owned: u.owned + 1, cost: Math.floor(u.cost * 1.4) }
          : u
      )
    );
  }, [upgrades, balance]);

  const btcAmount = (balance / 65000).toFixed(8);

  return (
    <section id="moneybutton" className="relative w-full py-20 md:py-32 overflow-hidden bg-dark-800">
      <div className="absolute inset-0 bg-gradient-to-b from-neon-purple/5 via-transparent to-neon-gold/5" />
      <div className="w-full px-4 md:px-6 lg:px-12 relative z-10">
        <div className="text-center mb-10 md:mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-neon-gold/10 border border-neon-gold/20 mb-4">
            <i className="ri-vip-crown-line text-neon-gold text-sm" />
            <span className="text-xs md:text-sm text-neon-gold font-medium">{t('moneyButton.zone')}</span>
          </div>

        </div>

        <div className="max-w-2xl mx-auto">
          <div className="p-6 md:p-10 rounded-3xl bg-dark-500/80 backdrop-blur-sm border border-white/5 text-center">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 mb-8">
              <div className="text-center">
                <div className="text-xs text-gray-500 mb-1">{t('moneyButton.balance')}</div>
                <div className="text-2xl md:text-3xl font-display font-bold text-white">
                  ${balance.toLocaleString()}
                </div>
                <div className="text-xs text-neon-gold mt-1">{t('moneyButton.btc')} {btcAmount}</div>
              </div>
              <div className="w-px h-12 bg-white/10 hidden sm:block" />
              <div className="flex gap-6">
                <div className="text-center">
                  <div className="text-xs text-gray-500 mb-1">{t('moneyButton.perClick')}</div>
                  <div className="text-lg font-display font-bold text-neon-blue">${perClick}</div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-gray-500 mb-1">{t('moneyButton.perSecond')}</div>
                  <div className="text-lg font-display font-bold text-neon-purple">${perSecond}</div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-gray-500 mb-1">{t('moneyButton.clicks')}</div>
                  <div className="text-lg font-display font-bold text-neon-gold">{clicks}</div>
                </div>
              </div>
            </div>

            <div className="relative flex justify-center mb-10">
              <button
                onClick={handleClick}
                className="relative w-28 h-28 md:w-36 md:h-36 rounded-full bg-gradient-to-br from-neon-gold via-yellow-500 to-orange-500 text-dark-900 font-bold text-xl md:text-2xl shadow-[0_0_40px_#F59E0B40] hover:shadow-[0_0_60px_#F59E0B60] active:scale-95 transition-all duration-150 cursor-pointer flex items-center justify-center"
              >
                <span className="flex items-center gap-1">
                  <i className="ri-money-dollar-circle-fill" /> {t('moneyButton.money')}
                </span>
                {particles.map((p) => (
                  <span
                    key={p.id}
                    className="absolute pointer-events-none text-neon-gold text-lg font-bold animate-bounce"
                    style={{ left: p.x, top: p.y, animation: 'floatUp 0.6s ease-out forwards' }}
                  >
                    +${perClick}
                  </span>
                ))}
              </button>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
              {upgrades.map((upgrade) => (
                <button
                  key={upgrade.id}
                  onClick={() => buyUpgrade(upgrade.id)}
                  disabled={balance < upgrade.cost}
                  className={`p-3 md:p-4 rounded-xl border text-left transition-all duration-300 cursor-pointer ${
                    balance >= upgrade.cost
                      ? 'bg-dark-400 border-white/10 hover:border-neon-gold/30 hover:-translate-y-0.5'
                      : 'bg-dark-500/50 border-white/5 opacity-50 cursor-not-allowed'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <i className={`${upgrade.icon} text-neon-gold text-lg`} />
                    <span className="text-xs text-gray-500">{t('moneyButton.owned')}{upgrade.owned}</span>
                  </div>
                  <div className="text-sm font-semibold text-white">{t(`moneyButton.upgrade_${upgrade.id}`)}</div>
                  <div className="text-xs text-neon-gold mt-1">${upgrade.cost.toLocaleString()}</div>
                  {upgrade.perClick > 0 && (
                    <div className="text-[10px] text-gray-500 mt-1">+${upgrade.perClick}{t('moneyButton.perClickLabel')}</div>
                  )}
                  {upgrade.perSecond > 0 && (
                    <div className="text-[10px] text-gray-500 mt-1">+${upgrade.perSecond}{t('moneyButton.perSecondLabel')}</div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes floatUp {
          0% { opacity: 1; transform: translateY(0) scale(1); }
          100% { opacity: 0; transform: translateY(-60px) scale(0.5); }
        }
      `}</style>
    </section>
  );
}
